import { useAppDispatch, useAppSelector } from '@core/store';
import { setRestrictionModal } from '@layouts/main-header/userSlice';
import { Org } from 'types/organization';

import DynamicPopup from './DynamicPopup';

import useComponentAction, {
  ComponentActionResult,
  ComponentActionResultKind
} from '../../hooks/useComponentAction';
import useCustomActionModalTarget from '../../hooks/useCustomActionModalTarget';
import useTrackDynamicView from '../../hooks/useTrackDynamicView';
import { advancePopupQueue } from '../../slices/dynamicComponentsSlice';
import { ComponentType } from '../../types';
import { dismiss } from '../../utils/dismissalPersistence';

interface DynamicPopupManagerProps {
  orgList: Org[];
  onSwitchAccount?: () => void;
}

const DynamicPopupManager = ({
  orgList,
  onSwitchAccount
}: DynamicPopupManagerProps) => {
  const dispatch = useAppDispatch();
  const { handleAction, isActionValid } = useComponentAction();
  const customModal = useCustomActionModalTarget();

  const { popups, isLoaded } = useAppSelector(
    (state) => state.dynamicComponents
  );

  const popup = popups[0];
  const { CustomModal } = customModal;

  useTrackDynamicView(popup, ComponentType.POPUP);

  if (!isLoaded) return null;

  const showPopupLayer = popups.length > 0 && popup !== undefined;

  const showPrimary =
    showPopupLayer &&
    !!popup.data.primary_button &&
    isActionValid(popup.data.primary_button);
  const showSecondary =
    showPopupLayer &&
    !!popup.data.secondary_button &&
    isActionValid(popup.data.secondary_button);

  const dismissCurrent = () => {
    if (!popup) return;
    if (popup.is_one_time) dismiss(ComponentType.POPUP, popup.id);
    dispatch(advancePopupQueue());
  };

  const finishPopupAction = (result: ComponentActionResult) => {
    if (!popup) return;
    if (result.kind === ComponentActionResultKind.DISMISS) {
      dismissCurrent();
      return;
    }
    if (result.kind === ComponentActionResultKind.CUSTOM) {
      dismissCurrent();
      customModal.setTargetId(result.target);
      return;
    }
    if (
      result.kind === ComponentActionResultKind.SIDE_EFFECT &&
      popup.is_dismissible
    ) {
      dismissCurrent();
    }
  };

  const onPrimaryClick = () => {
    if (!popup?.data.primary_button) return;
    finishPopupAction(handleAction(popup.data.primary_button));
  };

  const onSecondaryClick = () => {
    if (!popup?.data.secondary_button) return;
    finishPopupAction(handleAction(popup.data.secondary_button));
  };

  const onBeforeCreateAccount = () => {
    dismissCurrent();
    dispatch(setRestrictionModal(null));
  };

  if (!showPopupLayer && !CustomModal) return null;

  return (
    <>
      {showPopupLayer && popup && (
        <DynamicPopup
          instance={popup}
          showPrimary={showPrimary}
          showSecondary={showSecondary}
          onPrimaryClick={onPrimaryClick}
          onSecondaryClick={showSecondary ? onSecondaryClick : undefined}
          onClose={dismissCurrent}
          accountFooter={{
            orgList,
            onSwitchAccount,
            onBeforeCreateAccount
          }}
        />
      )}
      {CustomModal && <CustomModal onClose={customModal.clearTarget} />}
    </>
  );
};

export default DynamicPopupManager;
